/**
 * 每款遊戲的 **loading 遮罩主題色**（進度條填充 + 開始鈕，**同一個色**）。
 *
 * 🛑 **為什麼不放 `content.ts` 的 `chrome.themeColor`**：
 *   1. **loading 配色跟遊戲主題色是兩回事** —— 同一款可以各用各的。
 *   2. **走新 loading 流程的款不一定有 `content.ts`** —— 首款 `fcyxx` 就沒有（走 placeholder），
 *      放在 content 裡它根本設不到。
 *   → 所以獨立成這支小設定檔，跟「有沒有接 native help」完全解耦。
 *
 * 沒單獨設色的 gameId → 一律用 `LOADING_THEME_FALLBACK`（**`#d40101`**，使用者 2026-08-31）。
 * ⚠️ 因此 `var(--color-theme)` 已經**不會**被用到 —— 早期是「沒設就退回該款主題色」，
 * 但那樣每款顏色都不一樣、又沒人逐款定過色，實際效果是隨機。現在統一一個底色，
 * 要換色再逐款往 `LOADING_THEME` 補。（`LoadingScreen.vue` 的 CSS 仍留著
 * `var(..., var(--color-theme, #30a2d0))` 當最後保險，正常路徑碰不到。）
 *
 * ℹ️ 競品那側進度條與開始鈕**實際上是兩個不同深淺**（Emoji Riches `#B431F5` / `#9B05FF`），
 *    但我們定案是**兩處同色**（使用者 2026-08-20）→ 這裡只收一個值。
 *    真要分開再把型別改回 `{ bar, button }`，`resolveLoadingTheme` 是唯一的出口。
 * 🛑 **開始鈕的「文字」固定白色**，不吃這個色也不開旋鈕（同上日定案）——
 *    這裡設的是**底色**。
 *
 * 搭配的檔案：
 *   - 背景圖：`src/assets/loading/<gameId>/{zh,en}.webp`（丟檔即註冊，見 `useLoadingAssets.ts`）
 *   - 版面 / 其餘旋鈕：`src/components/LoadingScreen.vue` 的 `--loading-*`
 */

/**
 * gameId（小寫，同 URL 的 `?gameId=`）→ loading 主題色（CSS 色字串）。
 *
 * 📋 **值來自競品實測**：讀競品載入畫面 `.progress-bar-fill` 的 `background-color`
 *    計算值（2026-08-31 一次補齊 85 款）。可信度：`fcyxx` 抓到的值與先前 UI 定案的
 *    `#bc2de0` **完全相同**。
 *    ➜ **補新款不用另外做**：跑 `bash test/scrape.command` 抓 paytable/rules DOM 時
 *      會順手抓，結果印在最後、也寫在 `test/dom/<gameId>/loading_color.txt`，
 *      直接複製那一行貼進下面即可。
 *
 * ℹ️ `bjntt` / `sybs` 早期是**手選色**（取自 splash 標題描邊，`#32b0f3` / `#ec04ac`），
 *    2026-08-31 改成競品實測值（使用者決定，`--overwrite`）—— 全表統一為同一個來源。
 *
 * ✅ 87 款有 loading 圖的**全部都有色**（`mjhl2` / `xltb` 一度因 TSV 解析漏掉，見 read_urls）。
 * ⚠️ 其中 `lhzb` 是**取自載入圖的佔位值**（新款、當時無競品可實測），其餘 86 款都是競品實測值。
 *
 * ```ts
 * fcyxx: '#B431F5',
 * ```
 */
export const LOADING_THEME: Readonly<Record<string, string>> = {
  aeljl: '#ffc824', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  ajtmbd: '#a3314f', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  aldsd: '#da186f', // 競品 .progress-bar-fill 實測（2026-09-08 腳本抓取；原估值 #8842bb 取自載入圖主色）
  amhm: '#cd1247', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  anbs: '#5c45f5', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  bgyx: '#e01653', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  bhsj: '#ff0030', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  bjntt: '#347fee', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  bscq: '#8349da', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  bskg: '#483bac', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  bttj: '#ca4cff', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  bxdcg: '#0081fa', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  cjbqb: '#b431f5', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  cjgef: '#00aaff', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  csdxp: '#eb1313', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  dj: '#e01b5d', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  dlh: '#881ef5', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  dntg: '#db3c31', // 使用者提供（2026-09-07）
  dscj: '#e62e2e', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  dwjz: '#6440ff', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  dypd: '#00a3f5', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  fcyxx: '#bc2de0', // 2026-08-21 UI 定案（進度條填充 + 開始鈕同色）
  fhcq: '#ad051c', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  fyxcs: '#d3155c', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  fzdmx: '#cc0808', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  hbfy: '#8333b8', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  hbzdy: '#cc2934', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  hgyy: '#ff1f1f', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  hhsc: '#c320b6', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  hjyl: '#2e90e5', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  hljnh: '#de71fd', // 競品 .progress-bar-fill 實測（2026-09-14）
  jddts: '#94b842', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  jdry: '#f5571d', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  jdsry: '#0999e0', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  jlsb: '#ffc824', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  jqss: '#9e3dff', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  jqt: '#ff47c5', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  jsmjt: '#da4643', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  jsyj: '#8600e0', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  jymt: '#f0183c', // 競品 .progress-bar-fill 實測
  jzbc: '#ff4200', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  kldg: '#3d9cf5', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  kqll: '#5248e0', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  ljgf: '#9b05ff', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  lhzb: '#008aff', // 競品 .progress-bar-fill 實測（2026-09-03 腳本抓取，鏡像域）
  lrcq: '#00a5e0', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  lyqb: '#f0480d', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  mdcq: '#cc4b31', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  mhwcq: '#ff1f44', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  mjhl: '#eb1313', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  mjhl2: '#f54331', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  msmk: '#cc2934', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  msxrj: '#7740e6', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  mxgkh: '#21a8d9', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  nmzy: '#f5571d', // 競品 .progress-bar-fill 實測
  plst: '#b41fff', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  qlsb: '#e01b1b', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  rygz: '#b904ce', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  ryxc: '#d40101', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  rzvsws: '#de71fd', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  sbjn: '#ff0030', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  sgcl: '#70a200', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  shbz: '#e01b91', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  sjcz: '#960202', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  sjddj: '#cc0808', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  sjnw: '#d40101', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  slzq: '#4281cf', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  ssff: '#ffc824', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  sstsq: '#ff47ce', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  sswj: '#d62240', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  sxlm: '#e9aa00', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  sybs: '#684fff', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  szfkxz: '#2e5da3', // 競品 .progress-bar-fill 實測
  sysj: '#6358f5', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  thpsj: '#30a2d0', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  ttqj: '#ad33ff', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  tyscs: '#ab29cc', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  vjjy: '#f84d5a', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  wldd: '#da1886', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  xbhjc: '#2573b8', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  xcs: '#b56a4c', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  xgc: '#fe5a6d', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  xltb: '#eb1313', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  xltb2: '#29abcc', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  xltj: '#f0183c', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  xwytq: '#10a6cb', // 競品 .progress-bar-fill 實測（2026-09-03 腳本抓取，test/dom/xwytq/loading_color.txt）
  xyczl: '#01c259', // 競品 .progress-bar-fill 實測
  ycs: '#f53152', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  yjqj: '#098d95', // 競品 .progress-bar-fill 實測（2026-09-03 腳本抓取）
  yjzr: '#d62240', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  ysjl: '#0999e0', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  yyz3258: '#9933ff', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  yzjr: '#c320b6', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  zaqkl: '#e05a00', // 競品 .progress-bar-fill 實測（test/dom/zaqkl/loading_color.txt）
  zcm: '#e01b5d', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
  zzds: '#cc2114', // 競品 .progress-bar-fill 實測（2026-08-31 腳本抓取）
}

/**
 * 「背景圖還沒到」時 `.loading-frame` 的**保底底色**要混多少主題色進近黑底。
 *
 * 0 = 全黑、1 = 原主題色。純主題色（`#ec04ac` 這種高彩度洋紅）鋪滿整個直式版面太刺眼，
 * 2026-08-28 實測後取 **0.25**：看得出是這款的顏色、又不會亮到像跳出另一個畫面。
 */
const BG_FALLBACK_MIX = 0.25
/**
 * 混色的黑端**不用純 `#000`** —— 純黑鋪滿在 OLED 上會跟畫面外的黑連成一片，
 * 反而更像「還沒載到東西」。留一點點底讓它是「深色底」而不是「沒東西」。
 */
const BG_FALLBACK_BASE = 6

/**
 * 把主題色往近黑底混，得到 `.loading-frame` 的保底底色。
 *
 * 只認 `#rgb` / `#rrggbb`（`LOADING_THEME` 現有值都是這格式）；認不出來回 `undefined`
 * → 呼叫端就不設那個 CSS var → CSS 端 `var(--loading-bg-fallback, transparent)` 維持原本的透明。
 */
export function mixTowardBlack(color: string, mix: number = BG_FALLBACK_MIX): string | undefined {
  const m = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(color.trim())
  if (!m) return undefined
  let hex = m[1]!
  if (hex.length === 3) {
    hex = hex[0]! + hex[0]! + hex[1]! + hex[1]! + hex[2]! + hex[2]!
  }
  const out = [0, 2, 4].map((i) => {
    const v = parseInt(hex.slice(i, i + 2), 16)
    const mixed = Math.round(BG_FALLBACK_BASE + (v - BG_FALLBACK_BASE) * mix)
    return Math.max(0, Math.min(255, mixed)).toString(16).padStart(2, '0')
  })
  return '#' + out.join('')
}

/**
 * 沒在 `LOADING_THEME` 單獨設色的款一律用這個底色（使用者 2026-08-31）。
 * 逐款定色前的統一預設 —— 補進 `LOADING_THEME` 的款會蓋過它。
 */
export const LOADING_THEME_FALLBACK = '#d40101'

/**
 * 解析成要掛在 loading 遮罩（`.loading-frame`）上的 CSS vars。
 * **一定會回值**：該款沒單獨設色 → 用 `LOADING_THEME_FALLBACK`（`#d40101`）。
 *
 * `--loading-bg-fallback`（2026-08-28 新增）＝ **背景圖到達前的保底底色**。
 * 🛑 這條的存在理由是「永不黑屏」：新版版面切換的那一刻 `.loading-bg` 還沒下載完，
 *    `.loading-frame` 本來是透明的 → 露出 host 大底（實測是死灰 `#333`），
 *    看起來就是「畫面掛了」。有這個 var 就至少是一塊深色底。
 * ✅ 2026-08-31 起這件事**結構上保證**：`LOADING_THEME_FALLBACK` 讓每一款都拿得到色，
 *    所以 `--loading-bg-fallback` 一定有值，不會再有「沒設色 → 回空物件 → 露出死灰」的洞。
 *    （原本靠 `loadingTheme.spec.ts` 一條「有圖就必須設色」的測試提醒，已改成直接驗保證。）
 */
export function resolveLoadingTheme(gameId: string | null | undefined): Record<string, string> {
  const color = (gameId ? LOADING_THEME[gameId] : undefined) ?? LOADING_THEME_FALLBACK
  // 進度條填充與開始鈕**同色**（定案）
  const vars: Record<string, string> = { '--loading-bar-fill': color, '--loading-start-bg': color }
  const fallback = mixTowardBlack(color)
  if (fallback) vars['--loading-bg-fallback'] = fallback
  return vars
}
